import { useQueries, useQuery } from '@tanstack/react-query';

import { statsApi } from '@/services/stats';
import { workflowsApi } from '@/services/workflows';
import type { WorkflowStats } from '@/types/workflow';

export const statsKeys = {
  postsDaily: (days: number, cats?: number[]) =>
    ['stats', 'posts', 'daily', days, cats] as const,
  openai: (days: number) => ['stats', 'openai', days] as const,
  workflowStats: (id: string) => ['workflows', 'stats', id] as const,
};

export function usePostsDailyStats(days = 14, categories?: number[]) {
  return useQuery({
    queryKey: statsKeys.postsDaily(days, categories),
    queryFn: () => statsApi.postsDaily(days, categories),
    staleTime: 5 * 60_000,
  });
}

export function useOpenAiCostStats(days = 30) {
  return useQuery({
    queryKey: statsKeys.openai(days),
    queryFn: () => statsApi.openaiCost(days),
    staleTime: 60_000,
  });
}

/**
 * Fetch workflow stats for many workflows in parallel.
 * Uses useQueries so each one has its own loading state and cache.
 */
export function useStarredWorkflowsStats(ids: string[]) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: statsKeys.workflowStats(id),
      queryFn: () => workflowsApi.stats(id, 50),
      staleTime: 60_000,
    })),
    combine: (results) => ({
      data: results
        .map((r, i) => (r.data ? { id: ids[i], stats: r.data as WorkflowStats } : null))
        .filter(Boolean) as { id: string; stats: WorkflowStats }[],
      isLoading: results.some((r) => r.isLoading),
    }),
  });
}
